import { supabase } from '../lib/supabase';

export const characterSongAnalysisService = {
  /**
   * Get all character analyses for a specific song
   * @param {string} songId - Song UUID
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  getSongCharacterAnalyses: async (songId) => {
    try {
      if (!songId) {
        return { data: [], error: 'Song ID is required' };
      }

      // Query character_song_analyses directly with proper joins
      // This fixes the issue where song_roles junction table might not be populated
      const { data, error } = await supabase?.from('character_song_analyses')?.select(`
          *,
          role:roles!inner(
            id,
            character_name,
            voice_type,
            role_type,
            age_range,
            vocal_range,
            description,
            is_principal,
            musicals!inner(
              id,
              title,
              year_premiered
            )
          ),
          song:songs!inner(
            id,
            title,
            musical_id
          )
        `)?.eq('song_id', songId)?.order('role(character_name)');

      if (error) {
        console.error('Error fetching song character analyses:', error);
        return { data: [], error: error?.message || 'Failed to fetch character analyses' };
      }

      return { data: data || [], error: null };
    } catch (err) {
      console.error('Service error in getSongCharacterAnalyses:', err);
      return { data: [], error: err?.message || 'Failed to fetch character analyses' };
    }
  },

  /**
   * Get all characters assigned to a song (combines existing analyses + assigned roles without analyses)
   * This is the main method to use for the Edit Song modal
   * @param {string} songId - Song UUID
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  getAllSongCharacters: async (songId) => {
    try {
      if (!songId) {
        return { data: [], error: 'Song ID is required' };
      }

      // First, get all roles assigned to this song via song_roles junction table
      const { data: songRoles, error: rolesError } = await supabase
        ?.from('song_roles')
        ?.select(`
          id,
          is_solo,
          vocal_part,
          role:roles!inner(
            id,
            character_name,
            voice_type,
            role_type,
            age_range,
            vocal_range,
            description,
            is_principal,
            musicals!inner(
              id,
              title,
              year_premiered
            )
          )
        `)
        ?.eq('song_id', songId)
        ?.order('role(character_name)');

      if (rolesError) {
        console.error('Error fetching song roles:', rolesError);
        return { data: [], error: rolesError?.message || 'Failed to fetch assigned characters' };
      }

      if (!songRoles || songRoles?.length === 0) {
        return { data: [], error: null };
      }

      // Get existing character analyses for this song
      const { data: existingAnalyses } = await characterSongAnalysisService?.getSongCharacterAnalyses(songId);

      // Combine the data - for each assigned role, include existing analysis if available
      const combinedData = songRoles?.map(songRole => {
        // Look for existing analysis for this role
        const existingAnalysis = existingAnalyses?.find(analysis => 
          analysis?.role_id === songRole?.role?.id
        );

        return {
          // If analysis exists, use its ID, otherwise indicate this is a new character assignment
          id: existingAnalysis?.id || null,
          role_id: songRole?.role?.id,
          song_id: songId,
          
          // Analysis data (empty if no existing analysis)
          overview: existingAnalysis?.overview || {},
          level1: existingAnalysis?.level1 || '',
          level2: existingAnalysis?.level2 || '',
          level3: existingAnalysis?.level3 || [],
          level4: existingAnalysis?.level4 || {},
          level5: existingAnalysis?.level5 || {},
          phrases: existingAnalysis?.phrases || [],

          // Per-level Mux video playback IDs
          video_url_overview: existingAnalysis?.video_url_overview || null,
          video_url_level1: existingAnalysis?.video_url_level1 || null,
          video_url_level2: existingAnalysis?.video_url_level2 || null,
          video_url_level3: existingAnalysis?.video_url_level3 || null,
          video_url_level4: existingAnalysis?.video_url_level4 || null,
          video_url_level5: existingAnalysis?.video_url_level5 || null,
          
          // Song role info
          is_solo: songRole?.is_solo,
          vocal_part: songRole?.vocal_part,
          
          // Role info
          role: songRole?.role,
          
          // Metadata
          created_at: existingAnalysis?.created_at || null,
          updated_at: existingAnalysis?.updated_at || null,
          created_by: existingAnalysis?.created_by || null,
          updated_by: existingAnalysis?.updated_by || null,
          
          // Add flag to indicate if this has existing analysis data
          has_analysis: !!existingAnalysis
        };
      });

      return { data: combinedData || [], error: null };
    } catch (err) {
      console.error('Service error in getAllSongCharacters:', err);
      return { data: [], error: err?.message || 'Failed to fetch song characters' };
    }
  },

  /**
   * Get character analysis for specific song and role
   * @param {string} songId - Song UUID
   * @param {string} roleId - Role UUID
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  getCharacterAnalysis: async (songId, roleId) => {
    try {
      if (!songId || !roleId) {
        return { data: null, error: 'Song ID and Role ID are required' };
      }

      const { data, error } = await supabase?.from('character_song_analyses')?.select(`
          *,
          song:songs(id, title),
          role:roles(id, character_name, voice_type, role_type)
        `)?.eq('song_id', songId)?.eq('role_id', roleId)?.single();

      if (error && error?.code !== 'PGRST116') { // PGRST116 = not found
        console.error('Error fetching character analysis:', error);
        return { data: null, error: error?.message || 'Failed to fetch character analysis' };
      }

      return { data: data || null, error: null };
    } catch (err) {
      console.error('Service error in getCharacterAnalysis:', err);
      return { data: null, error: err?.message || 'Failed to fetch character analysis' };
    }
  },

  /**
   * Get or create character analysis for editing
   * @param {string} songId - Song UUID
   * @param {string} roleId - Role UUID
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  getOrCreateCharacterAnalysis: async (songId, roleId, userId) => {
    try {
      if (!songId || !roleId) {
        return { data: null, error: 'Song ID and Role ID are required' };
      }

      // First try to get existing analysis
      const existingResult = await characterSongAnalysisService?.getCharacterAnalysis(songId, roleId);
      
      if (existingResult?.data) {
        return existingResult; // Return existing analysis
      }

      // If no existing analysis, create a new one with empty data
      const createResult = await characterSongAnalysisService?.createCharacterAnalysis({
        song_id: songId,
        role_id: roleId,
        overview: {},
        level1: '',
        level2: '',
        level3: [],
        level4: {},
        level5: {},
        phrases: [],
        created_by: userId
      });

      return createResult;
    } catch (err) {
      console.error('Service error in getOrCreateCharacterAnalysis:', err);
      return { data: null, error: err?.message || 'Failed to get or create character analysis' };
    }
  },

  /**
   * Create new character analysis
   * @param {Object} analysisData - Analysis data
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  createCharacterAnalysis: async (analysisData) => {
    try {
      const { data, error } = await supabase?.from('character_song_analyses')?.insert([{
          song_id: analysisData?.song_id,
          role_id: analysisData?.role_id,
          overview: analysisData?.overview || {},
          level1: analysisData?.level1 || '',
          level2: analysisData?.level2 || '',
          level3: analysisData?.level3 || [],
          level4: analysisData?.level4 || {},
          level5: analysisData?.level5 || {},
          phrases: analysisData?.phrases || [],
          video_url_overview: analysisData?.video_url_overview || null,
          video_url_level1: analysisData?.video_url_level1 || null,
          video_url_level2: analysisData?.video_url_level2 || null,
          video_url_level3: analysisData?.video_url_level3 || null,
          video_url_level4: analysisData?.video_url_level4 || null,
          video_url_level5: analysisData?.video_url_level5 || null,
          created_by: analysisData?.created_by
        }])?.select()?.single();

      if (error) {
        console.error('Error creating character analysis:', error);
        return { data: null, error: error?.message || 'Failed to create character analysis' };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in createCharacterAnalysis:', err);
      return { data: null, error: err?.message || 'Failed to create character analysis' };
    }
  },

  /**
   * Update existing character analysis
   * @param {string} analysisId - Analysis UUID
   * @param {Object} analysisData - Updated analysis data
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  updateCharacterAnalysis: async (analysisId, analysisData) => {
    try {
      if (!analysisId) {
        return { data: null, error: 'Analysis ID is required' };
      }

      const { data, error } = await supabase?.from('character_song_analyses')?.update({
          overview: analysisData?.overview,
          level1: analysisData?.level1,
          level2: analysisData?.level2,
          level3: analysisData?.level3,
          level4: analysisData?.level4,
          level5: analysisData?.level5,
          phrases: analysisData?.phrases,
          video_url_overview: analysisData?.video_url_overview !== undefined ? analysisData?.video_url_overview : undefined,
          video_url_level1: analysisData?.video_url_level1 !== undefined ? analysisData?.video_url_level1 : undefined,
          video_url_level2: analysisData?.video_url_level2 !== undefined ? analysisData?.video_url_level2 : undefined,
          video_url_level3: analysisData?.video_url_level3 !== undefined ? analysisData?.video_url_level3 : undefined,
          video_url_level4: analysisData?.video_url_level4 !== undefined ? analysisData?.video_url_level4 : undefined,
          video_url_level5: analysisData?.video_url_level5 !== undefined ? analysisData?.video_url_level5 : undefined,
          updated_by: analysisData?.updated_by
        })?.eq('id', analysisId)?.select()?.single();

      if (error) {
        console.error('Error updating character analysis:', error);
        return { data: null, error: error?.message || 'Failed to update character analysis' };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Service error in updateCharacterAnalysis:', err);
      return { data: null, error: err?.message || 'Failed to update character analysis' };
    }
  },

  /**
   * Delete character analysis
   * @param {string} analysisId - Analysis UUID
   * @returns {Promise<{success: boolean, error: string|null}>}
   */
  deleteCharacterAnalysis: async (analysisId) => {
    try {
      if (!analysisId) {
        return { success: false, error: 'Analysis ID is required' };
      }

      const { error } = await supabase?.from('character_song_analyses')?.delete()?.eq('id', analysisId);

      if (error) {
        console.error('Error deleting character analysis:', error);
        return { success: false, error: error?.message || 'Failed to delete character analysis' };
      }

      return { success: true, error: null };
    } catch (err) {
      console.error('Service error in deleteCharacterAnalysis:', err);
      return { success: false, error: err?.message || 'Failed to delete character analysis' };
    }
  }
};

export default characterSongAnalysisService;